import * as React from 'react';
import { Form, Icon as LegacyIcon } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Input, Alert, Button, Modal, Select, InputNumber, message, Switch } from 'antd';
import { connect } from 'react-redux';
import { FormComponentProps } from '@ant-design/compatible/lib/form/Form';
import { withRouter } from 'react-router';
import { getCurrency, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import BackButton from '../back_button/BackButton';
import { stockLocationsRestCrudThunksForSupplier } from '../../thunks/stock_locations_thunks';
import { getBackendUri } from '../../utils/EnvConfigUtils';
import { getProtectedImageUriForSupplier } from '../../utils/FileAccessUtils';
import { imageUploadValidation, normFile } from '../../utils/ImageUtils';
import { equipmentTypesRestCrudThunksForSupplier } from '../../thunks/equipment_types_thunks';
import { vendorsRestCrudThunksForSupplier } from '../../thunks/vendors_thunks';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { OWButton } from '../ow_button/OWButton';
import { replaceEquipmentVendorsForSupplier } from '../../thunks/equipment_catalogs_thunks';
import { retrieveCachedUserDetails } from '../../thunks/session_thunks';
import {
	assetTypesRestCrudThunksForSupplier,
	fetchAssetTypesLiteForSupplier,
} from '../../thunks/asset_types_thunks';
import { PERMISSION_NAMES, isInternalTech, isSupplierAllowedToAccess } from '../../utils/AuthUtils';
import OWUpload from '../OWUpload';
import ClearErrors from '../clear_errors/ClearErrors';
import { EQUIPMENT_TYPES_CRUD_ACTION_CREATORS } from '../../actions/equipment_types_actions';
import { isGLCodeAllowedBySettings } from '../../utils/InventoryUtils';

require('./EquipmentForm.less');

interface EquipmentFormProps extends FormComponentProps {
	creating: boolean;
	updating: boolean;
	formData: any;
	history: any;
	redirectForwardUrl?: string;
	onSuccess?: any;
	createErrors: any[];
	updateErrors: any[];
	currentUser: any;
	create: any;
	update: any;
	stockLocations: any;
	fetchStockLocations: any;
	vendors: any;
	fetchMultipleVendors: any;
	fetchVendors: any;
	replaceVendors: any;
	createVendor: any;
	fetchMultipleAssetTypes: any;
	fetchAllAssetTypesLite: any;
	assetTypes: any;
	onCancel?: any;
	companyConfig: any;
}

class EquipmentForm extends React.Component<EquipmentFormProps, any> {
	state = {
		previewVisible: false,
		uploadLoading: false,
		previewImage: null,
		allPhotos: [],
		submitting: false,
	};

	componentDidMount() {
		const { fetchStockLocations, formData } = this.props;
		fetchStockLocations({});

		let photosInfo;
		if (formData.images) {
			photosInfo = formData.images.map((p, idx) => {
				const [imgFileId, imgFileName] = p.split('/');
				const thumbUri = getProtectedImageUriForSupplier(imgFileId, imgFileName, 100, 100);
				const fullUri = getProtectedImageUriForSupplier(imgFileId, imgFileName, 400, 400);
				return {
					uid: idx,
					size: 1,
					name: imgFileName,
					filename: imgFileName,
					url: fullUri,
					status: 'done',
					thumbUrl: thumbUri,
					response: { data: { fileName: imgFileName, fileId: imgFileId } },
					type: 'image/jpeg',
				};
			});
		} else {
			photosInfo = [];
		}
		this.setState({ allPhotos: photosInfo });
	}

	onSubmitSuccess = (record) => {
		const { history, onSuccess, redirectForwardUrl } = this.props;
		if (redirectForwardUrl) {
			history.push(redirectForwardUrl);
		}
		if (onSuccess) {
			onSuccess(record);
		}
	};

	handleSubmit = (e, isUpdate) => {
		const { replaceVendors, currentUser, update, create, formData } = this.props;
		e.preventDefault();
		this.props.form.validateFields((err, values) => {
			if (!err) {
				this.setState({ submitting: true });

				const submitAction = isUpdate ? update : create;

				const picturesInfo = values.images.map((p) => {
					const fileName = nullSafeGetOrElse('response.data.fileName', p, '');
					const fileId = nullSafeGetOrElse('response.data.fileId', p, '');
					return `${fileId}/${fileName}`;
				});

				const allVendorIds = Array.from(
					new Set([
						...nullSafeGetOrElse('partEquipmentVendorIds', values, []),
						...(values.defaultVendorId ? [values.defaultVendorId] : []),
					])
				);

				const equipmentCatalogs = allVendorIds.map((vendorId) => {
					return {
						partEquipmentVendorId: vendorId,
						currencyId: values.currencyId || 'USD',
						supplierFacilityId: values.supplierFacilityId,
						supplierCompanyId: values.supplierCompanyId,
					};
				});

				const equipment = {
					...formData,
					id: values.id,
					modelName: values.modelName,
					isActive: values.isActive,
					equipmentTypeNumber: values.equipmentTypeNumber,
					aliasEquipmentTypeNumber: values.aliasEquipmentTypeNumber,
					glCode: values.glCode,
					description: values.description,
					images: picturesInfo,
					manufacturerId: values.manufacturerId,
					manufacturerName: values.manufacturerName,
					...(values.cost && { cost: parseFloat(values.cost) }),
					...(isInternalTech(currentUser) && { assetTypeId: values.assetTypeId }),
					supplierFacilityId: values.supplierFacilityId,
					supplierCompanyId: values.supplierCompanyId,
					defaultVendorId: values.defaultVendorId || formData.defaultVendorId,
				};

				submitAction(equipment)
					.then((record) => {
						if (!isUpdate && equipmentCatalogs.length > 0) {
							const updatedPartCatalog = equipmentCatalogs.map((equipmentCatalog) => {
								return {
									...equipmentCatalog,
									equipmentTypeId: record.id,
								};
							});
							replaceVendors(record.id, updatedPartCatalog)
								.then((_) => this.onSubmitSuccess(record))
								.catch((err) => message.error(err))
								.finally(() => this.setState({ submitting: true }));
						} else {
							this.setState({ submitting: false });
							this.onSubmitSuccess(record);
						}
					})
					.catch((err) => {
						this.setState({ submitting: false });
						message.error(err);
					});
			}
		});
	};

	handlePreview = (file) => {
		this.setState({
			previewImage: file.url || file.thumbUrl,
			previewVisible: true,
		});
	};

	handleCancel = () => this.setState({ previewVisible: false });

	handleImageUpdate = (info) => {
		if (info.file.status === 'done') {
			this.setState({ allPhotos: info.fileList });
		} else if (info.file.status === undefined) {
			info.fileList = this.state.allPhotos;
		}
	};

	render() {
		const {
			form,
			createErrors,
			formData,
			updateErrors,
			currentUser,
			vendors,
			fetchMultipleVendors,
			fetchVendors,
			createVendor,
			fetchMultipleAssetTypes,
			fetchAllAssetTypesLite,
			assetTypes,
			onCancel,
			companyConfig,
		} = this.props;
		const { previewVisible, previewImage } = this.state;
		const { getFieldDecorator, getFieldValue } = form;
		const isUpdate = formData && formData.id !== undefined;
		const submitText = isUpdate ? 'Update equipment type' : 'Create equipment type';
		const currency = getCurrency({ currentUser });

		const BACKEND_URI = getBackendUri();
		const uploadHeaders = { 'X-Auth-Token': retrieveCachedUserDetails(['token']).token };

		getFieldDecorator('supplierFacilityId', {
			initialValue: formData.supplierFacilityId || nullSafeGet('facility.id', currentUser),
		});
		getFieldDecorator('supplierCompanyId', {
			initialValue:
				formData.supplierCompanyId || nullSafeGet('facility.supplierCompanyId', currentUser),
		});

		getFieldDecorator('id', { initialValue: formData.id });

		const canCreateVendors = isSupplierAllowedToAccess(
			PERMISSION_NAMES.MODIFY_VENDORS,
			companyConfig,
			currentUser.roles
		);

		const canShowCost =
			nullSafeGetOrElse('partEquipmentVendorCount', formData, 0) <= 0 &&
			nullSafeGetOrElse('length', getFieldValue('partEquipmentVendorIds'), 0) <= 0 &&
			!getFieldValue('defaultVendorId');

		const isImageUploading = (getFieldValue('images') || []).some((_) => _.status === 'uploading');
		//TODO: UPGRADE FORM
		return (
			<Form
				layout="vertical"
				onSubmit={(e) => this.handleSubmit(e, isUpdate)}
				className="supplierForm"
			>
				<ClearErrors actionCreators={EQUIPMENT_TYPES_CRUD_ACTION_CREATORS} />
				{createErrors.length > 0 ? (
					<Form.Item>
						<Alert message={createErrors.join(' ')} type="error" />
					</Form.Item>
				) : null}
				{updateErrors.length > 0 ? (
					<Form.Item>
						<Alert message={updateErrors.join(' ')} type="error" />
					</Form.Item>
				) : null}
				<Form.Item label="Model Name">
					{getFieldDecorator('modelName', {
						initialValue: formData.modelName,
						rules: [{ required: true, message: 'This field is required.' }],
					})(<Input style={{ maxWidth: 640 }} />)}
				</Form.Item>
				<Form.Item label="Is Active">
					{getFieldDecorator('isActive', {
						initialValue: formData.isActive === undefined ? true : formData.isActive,
						valuePropName: 'checked',
					})(<Switch />)}
				</Form.Item>
				<Form.Item label="Equipment Type Number">
					{getFieldDecorator('equipmentTypeNumber', {
						initialValue: formData.equipmentTypeNumber,
					})(<Input style={{ maxWidth: 640 }} />)}
				</Form.Item>
				<Form.Item label="Alias Equipment Type Number">
					{getFieldDecorator('aliasEquipmentTypeNumber', {
						initialValue: formData.aliasEquipmentTypeNumber,
					})(<Input style={{ maxWidth: 640 }} />)}
				</Form.Item>
				{isGLCodeAllowedBySettings(companyConfig) ? (
					<Form.Item label={'GL Code'}>
						{getFieldDecorator('glCode', {
							initialValue: formData.glCode,
						})(<Input style={{ maxWidth: 640 }} />)}
					</Form.Item>
				) : null}
				{isInternalTech(currentUser) ? (
					<Form.Item label="Asset type">
						{getFieldDecorator('assetTypeId', {
							initialValue: formData.assetTypeId,
						})(
							<OWAsyncSelect
								style={{ maxWidth: 640 }}
								stateSlice={assetTypes}
								targetCollectionName="assetTypesIndex"
								fetchMultiple={(ids, targetCollectionName) => {
									fetchMultipleAssetTypes(ids, targetCollectionName);
								}}
								fetchData={(
									searchText,
									targetCollectionName,
									pagination,
									sorting,
									filters,
									addToTargetCollection
								) => {
									fetchAllAssetTypesLite(
										{ search: searchText },
										targetCollectionName,
										pagination,
										sorting,
										filters,
										addToTargetCollection
									);
								}}
								placeholder={'Select Asset Type'}
								renderRecord={(assetType) => (
									<Select.Option key={assetType.id} value={assetType.id}>
										{assetType.name}
									</Select.Option>
								)}
							/>
						)}
					</Form.Item>
				) : null}
				<Form.Item label="Description">
					{getFieldDecorator('description', {
						initialValue: formData.description,
					})(<Input.TextArea style={{ maxWidth: 640 }} />)}
				</Form.Item>
				{isUpdate ? null : (
					<>
						<Form.Item label="Vendors">
							{getFieldDecorator('partEquipmentVendorIds', {
								initialValue: formData.partEquipmentVendorIds,
							})(
								<OWAsyncSelect
									style={{ maxWidth: 640 }}
									mode="multiple"
									stateSlice={vendors}
									targetCollectionName="EQUIPMENT_VENDORS_AUTOCOMPLETE"
									fetchMultiple={(ids, targetCollectionName) => {
										fetchMultipleVendors(ids, targetCollectionName);
									}}
									fetchData={(
										searchText,
										targetCollectionName,
										pagination,
										sorting,
										filters,
										addToTargetCollection
									) =>
										fetchVendors(
											{ name: searchText },
											targetCollectionName,
											pagination,
											sorting,
											filters,
											addToTargetCollection
										)
									}
									createNewEntity={
										canCreateVendors
											? (name) => createVendor({ name, currencyId: 'USD' })
											: undefined
									}
									placeholder={'Select Vendors'}
									renderRecord={(vendor) => (
										<Select.Option key={vendor.id} value={vendor.id}>
											{vendor.name}
										</Select.Option>
									)}
								/>
							)}
						</Form.Item>
						<Form.Item label="Default Vendor">
							{getFieldDecorator('defaultVendorId', {
								initialValue: formData.defaultVendorId,
							})(
								<OWAsyncSelect
									style={{ maxWidth: 640 }}
									stateSlice={vendors}
									targetCollectionName="defaultPartEquipmentVendors"
									fetchMultiple={(ids, targetCollectionName) => {
										fetchMultipleVendors(ids, targetCollectionName);
									}}
									fetchData={(
										searchText,
										targetCollectionName,
										pagination,
										sorting,
										filters,
										addToTargetCollection
									) =>
										fetchVendors(
											{ name: searchText },
											targetCollectionName,
											pagination,
											sorting,
											filters,
											addToTargetCollection
										)
									}
									placeholder={'Select Default Vendor'}
									createNewEntity={
										canCreateVendors
											? (name) => createVendor({ name, currencyId: 'USD' })
											: undefined
									}
									renderRecord={(vendor) => (
										<Select.Option key={vendor.id} value={vendor.id}>
											{vendor.name}
										</Select.Option>
									)}
								/>
							)}
						</Form.Item>
					</>
				)}
				<Form.Item label="Manufacturer Name">
					{getFieldDecorator('manufacturerName', {
						initialValue: formData.manufacturerName,
					})(<Input style={{ maxWidth: 640 }} />)}
				</Form.Item>
				<Form.Item label="Manufacturer Id">
					{getFieldDecorator('manufacturerId', {
						initialValue: formData.manufacturerId,
					})(<Input style={{ maxWidth: 640 }} />)}
				</Form.Item>
				{canShowCost ? (
					<Form.Item label="Purchase Price">
						{getFieldDecorator('cost', {
							initialValue: formData.cost,
						})(
							<InputNumber
								style={{ minWidth: 120 }}
								min={0.0}
								formatter={(value) => `${value}`}
								precision={2}
								step={0.01}
								addonAfter={currency.id}
							/>
						)}
					</Form.Item>
				) : null}
				<Form.Item label="Photos">
					{getFieldDecorator(`images`, {
						valuePropName: 'fileList',
						getValueFromEvent: normFile,
						initialValue: this.state.allPhotos,
					})(
						<OWUpload
							accept="image/*"
							name="file"
							listType="picture-card"
							className="avatar-uploader"
							multiple={true}
							headers={uploadHeaders}
							onPreview={this.handlePreview}
							action={`${BACKEND_URI}/api/v1/supplier/file/upload`}
							beforeUpload={imageUploadValidation}
							onChange={this.handleImageUpdate}
						>
							<div style={{ display: 'inline' }}>
								<LegacyIcon type={this.state.uploadLoading ? 'loading' : 'plus'} />
								<div className="ant-upload-text">Upload</div>
							</div>
							<Modal visible={previewVisible} footer={null} onCancel={this.handleCancel}>
								<img alt="example" style={{ width: '100%' }} src={previewImage} />
							</Modal>
						</OWUpload>
					)}
				</Form.Item>
				<Form.Item>
					<OWButton
						type="primary"
						htmlType="submit"
						loading={this.state.submitting || isImageUploading}
						className="contactForm__button"
					>
						{submitText}
					</OWButton>
					<span style={{ marginLeft: '16px' }}>
						{onCancel ? (
							<Button type="ghost" onClick={onCancel}>
								Cancel
							</Button>
						) : (
							<BackButton buttonText="Cancel" />
						)}
					</span>
				</Form.Item>
			</Form>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	formData: ownProps.formData,
	redirectForwardUrl: ownProps.redirectForwardUrl,
	onSuccess: ownProps.onSuccess,
	assetTypes: state.asset_types,

	createErrors: state.equipment_types.createErrors,
	updateErrors: state.equipment_types.updateErrors,
	creating: state.equipment_types.creating,
	updating: state.equipment_types.updating,
	stockLocations: state.stock_locations,
	currentUser: state.session.currentUser,
	vendors: state.vendors,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch) => ({
	update: (entity) => dispatch(equipmentTypesRestCrudThunksForSupplier.update(entity)),
	create: (entity) => dispatch(equipmentTypesRestCrudThunksForSupplier.create(entity)),
	replaceVendors: (partId, entities) =>
		dispatch(replaceEquipmentVendorsForSupplier(partId, entities)),
	fetchStockLocations: (params) => dispatch(stockLocationsRestCrudThunksForSupplier.read(params)),
	fetchVendors: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			vendorsRestCrudThunksForSupplier.read(
				{ ...(params || {}), isActive: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleVendors: (ids, targetCollectionName) =>
		dispatch(vendorsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	createVendor: (entity) => dispatch(vendorsRestCrudThunksForSupplier.create(entity)),
	fetchMultipleAssetTypes: (ids, targetCollectionName) =>
		dispatch(assetTypesRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchAllAssetTypesLite: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			fetchAssetTypesLiteForSupplier(
				{ ...params, no_pagination: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
});

export default withRouter(
	connect(mapStateToProps, mapDispatchToProps)(Form.create<EquipmentFormProps>()(EquipmentForm))
);
